import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { apiAlova } from "../lib/alova";

/* ==================== Types ==================== */

export interface Unit {
  id_unit_kerja: string;
  name: string;
  code?: string;
  label?: string;
  value?: string | number;
  [key: string]: any;
}

interface UnitsContextType {
  units: Unit[];
  selectedUnit: Unit | null;
  loading: boolean;
  fetchUnits: (group: number, userUnits?: string[], defaultUnit?: string) => Promise<void>;
  setSelectedUnit: (unit: Unit | null) => void;
}

/* ==================== Context ==================== */

const UnitsContext = createContext<UnitsContextType | null>(null);

/* ==================== Cache ==================== */

let fetchCache = {
  key: null as string | null,
  data: null as Unit[] | null,
  timestamp: null as number | null,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const generateCacheKey = (group: number, userUnits?: string[]): string => {
  const units = userUnits || [];
  return `${group}-${units.sort().join(',')}`;
};

const isCacheValid = (cacheKey: string): boolean => {
  if (!fetchCache.key || fetchCache.key !== cacheKey) return false;
  if (!fetchCache.timestamp) return false;
  return Date.now() - fetchCache.timestamp < CACHE_DURATION;
};

/* ==================== Provider ==================== */

interface UnitsProviderProps {
  children: ReactNode;
}

export function UnitsProvider({ children }: UnitsProviderProps) {
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchUnits = useCallback(async (group: number, userUnits?: string[], defaultUnit?: string) => {
    const cacheKey = generateCacheKey(group, userUnits);

    // Return cached data if still valid
    if (isCacheValid(cacheKey) && fetchCache.data) {
      console.log("Using cached units data");
      setUnits(fetchCache.data);
      return;
    }

    setLoading(true);
    try {
      const res = await apiAlova.Post<{ data: Unit[] }>(`presensi/master/unit/list`, {
        group,
      }).send();

      if (res && res.data) {
        const unitsData = res.data;
        const processedUnits = unitsData.map((unit) => {
          return {
            ...unit,
            label: unit.name,
            value: unit.id_unit_kerja,
          };
        });
        const hasAllUnit = [1, 2].includes(group);
        if (hasAllUnit) {
          processedUnits.unshift({
            id_unit_kerja: "0",
            name: "Semua Unit",
            value: "0",
            label: "Semua Unit",
          });
        }
        
        const defaultUnitData = hasAllUnit 
          ? processedUnits.find((unit) => unit?.value === "0")
          : processedUnits.find((unit) => unit?.value === defaultUnit);
        setSelectedUnit(defaultUnitData || null);

        setUnits(processedUnits);
        // Update cache
        fetchCache = {
          key: cacheKey,
          data: processedUnits,
          timestamp: Date.now(),
        };
      }
    } catch (err) {
      console.error("Failed to fetch units:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const value: UnitsContextType = {
    units,
    selectedUnit,
    loading,
    fetchUnits,
    setSelectedUnit,
  };

  return (
    <UnitsContext.Provider value={value}>
      {children}
    </UnitsContext.Provider>
  );
}

/* ==================== Hook ==================== */

export function useUnits(): UnitsContextType {
  const ctx = useContext(UnitsContext);
  if (!ctx) {
    throw new Error("useUnits must be used inside UnitsProvider");
  }
  return ctx;
}
